import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { ArtworkCard } from "@/components/ArtworkCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Search, X } from "lucide-react";

export const ShopPage = () => {
  const [artworks, setArtworks] = useState([]);
  const [filteredArtworks, setFilteredArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seasonFilter, setSeasonFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchArtworks = async () => {
      try {
        const response = await axios.get(`${API}/artworks`);
        setArtworks(response.data);
        setFilteredArtworks(response.data);
      } catch (e) {
        console.error("Error fetching artworks:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchArtworks();
  }, []);

  useEffect(() => {
    let filtered = [...artworks];

    if (seasonFilter !== "all") {
      filtered = filtered.filter((a) => a.season === seasonFilter);
    }

    if (yearFilter !== "all") {
      filtered = filtered.filter((a) => a.year === parseInt(yearFilter));
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.title.toLowerCase().includes(query) ||
          a.description.toLowerCase().includes(query) ||
          a.artist.toLowerCase().includes(query)
      );
    }

    setFilteredArtworks(filtered);
  }, [artworks, seasonFilter, yearFilter, searchQuery]);

  const clearFilters = () => {
    setSeasonFilter("all");
    setYearFilter("all");
    setSearchQuery("");
  };

  const years = [...new Set(artworks.map((a) => a.year))].sort((a, b) => b - a);
  const hasFilters = seasonFilter !== "all" || yearFilter !== "all" || searchQuery;

  return (
    <div className="py-12 md:py-16 px-6 md:px-12 lg:px-24 max-w-screen-2xl mx-auto" data-testid="shop-page">
      {/* Header */}
      <div className="mb-12">
        <p className="text-sm uppercase tracking-widest text-muted-foreground mb-2">Browse</p>
        <h1 className="font-serif text-4xl md:text-5xl font-normal tracking-tight">All Artwork</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-12">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search artwork..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-none border-border bg-transparent focus:ring-0 focus:border-primary"
            data-testid="search-input"
          />
        </div>

        {/* Season Filter */}
        <Select value={seasonFilter} onValueChange={setSeasonFilter}>
          <SelectTrigger className="w-full md:w-40 rounded-none" data-testid="season-filter">
            <SelectValue placeholder="Season" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Seasons</SelectItem>
            <SelectItem value="spring">Spring</SelectItem>
            <SelectItem value="summer">Summer</SelectItem>
            <SelectItem value="fall">Fall</SelectItem>
            <SelectItem value="winter">Winter</SelectItem>
          </SelectContent>
        </Select>

        {/* Year Filter */}
        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger className="w-full md:w-32 rounded-none" data-testid="year-filter">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Years</SelectItem>
            {years.map((year) => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Clear Filters */}
        {hasFilters && (
          <Button
            variant="outline"
            onClick={clearFilters}
            className="rounded-none gap-2"
            data-testid="clear-filters-btn"
          >
            <X className="w-4 h-4" />
            Clear
          </Button>
        )}
      </div>

      {/* Results count */}
      <p className="text-sm text-muted-foreground mb-8">
        Showing {filteredArtworks.length} {filteredArtworks.length === 1 ? "artwork" : "artworks"}
      </p>

      {/* Artwork Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="aspect-[3/4] bg-muted animate-pulse" />
          ))}
        </div>
      ) : filteredArtworks.length === 0 ? (
        <div className="text-center py-24">
          <p className="text-muted-foreground mb-4">No artwork found matching your criteria.</p>
          <Button
            variant="outline"
            onClick={clearFilters}
            className="rounded-none"
            data-testid="no-results-clear-btn"
          >
            Clear Filters
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
          {filteredArtworks.map((artwork, index) => (
            <ArtworkCard key={artwork.id} artwork={artwork} index={index} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ShopPage;
